import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { asset, mediaUrl } from "@/lib/api";

export function Btn({ to, href, children, variant = "primary", className = "", testId, ...rest }) {
  const styles = {
    primary:
      "bg-bka-emerald text-white hover:bg-bka-emeraldDark hover:shadow-[0_12px_30px_-14px_rgba(0,140,114,0.85)]",
    gold: "bg-bka-gold text-white hover:bg-bka-coralDark hover:shadow-[0_12px_30px_-14px_rgba(223,157,55,0.9)]",
    action:
      "bg-bka-orange text-white hover:bg-bka-orangeDark hover:shadow-[0_14px_32px_-14px_rgba(143,55,3,0.95)]",
    outlineAction:
      "border-2 border-bka-orange text-bka-orangeDark hover:bg-bka-orange hover:text-white",
    outline: "border-2 border-bka-emerald text-bka-emerald hover:bg-bka-emerald hover:text-white",
    light: "border-2 border-white/40 text-white hover:border-white hover:bg-white/10",
    navy: "bg-bka-navy text-white hover:bg-bka-navyDeep",
  }[variant];

  const cls = `group inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-bold transition-all duration-300 ${styles} ${className}`;
  const inner = (
    <>
      {children}
      <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
    </>
  );

  if (to) return <Link to={to} data-testid={testId} className={cls} {...rest}>{inner}</Link>;
  if (href)
    return (
      <a href={href} data-testid={testId} className={cls} {...rest}>
        {inner}
      </a>
    );
  return (
    <button type="button" data-testid={testId} className={cls} {...rest}>
      {inner}
    </button>
  );
}

export function SectionHead({ overline, title, text, align = "left", tone = "light" }) {
  const alignCls = align === "center" ? "text-center mx-auto max-w-2xl" : "";
  return (
    <div className={`${alignCls} mb-10 lg:mb-14`}>
      {overline && (
        <p className={`bka-overline mb-3 ${tone === "dark" ? "text-bka-orangeLight" : "text-bka-emerald"}`}>
          {overline}
        </p>
      )}
      <h2
        className={`font-display text-2xl font-bold leading-[1.15] sm:text-3xl lg:text-4xl ${
          tone === "dark" ? "text-white" : "text-bka-navy"
        }`}
      >
        {title}
      </h2>
      {text && (
        <p className={`mt-4 text-base leading-relaxed ${tone === "dark" ? "text-white/70" : "text-bka-ink/80"}`}>
          {text}
        </p>
      )}
      <div className={`bka-accent-rule mt-6 ${align === "center" ? "mx-auto" : ""}`} />
    </div>
  );
}

export function PageHero({ overline, line1, line2, subheading, image, tone = "emerald" }) {
  return (
    <section
      data-testid="page-hero"
      className={`relative overflow-hidden ${tone === "emerald" ? "bg-[#032019]" : "bg-bka-emBg"}`}
    >
      {image && (
        <img
          src={mediaUrl(image)}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover opacity-45"
        />
      )}
      <div
        className="absolute inset-0"
        aria-hidden="true"
        style={{
          background:
            tone === "emerald"
              ? "linear-gradient(105deg, rgba(3,32,25,0.95) 0%, rgba(0,80,47,0.84) 45%, rgba(0,107,63,0.58) 100%)"
              : "linear-gradient(105deg, rgba(4,47,44,0.96) 0%, rgba(8,61,58,0.85) 50%, rgba(0,140,114,0.45) 100%)",
        }}
      />
      <div className="bka-container relative py-16 sm:py-20 lg:py-28">
        {overline && <p className="bka-overline mb-4 text-bka-orangeLight bka-reveal">{overline}</p>}
        <h1
          className="max-w-3xl font-display text-4xl font-bold leading-[1.05] text-white bka-reveal sm:text-5xl lg:text-6xl"
          style={{ animationDelay: "80ms" }}
        >
          {line1}
          {line2 ? (
            <>
              <br />
              <span className="text-bka-aqua">{line2}</span>
            </>
          ) : null}
        </h1>
        {subheading && (
          <p
            className="mt-6 max-w-2xl text-base leading-relaxed text-white/80 bka-reveal md:text-lg"
            style={{ animationDelay: "160ms" }}
          >
            {subheading}
          </p>
        )}
      </div>
    </section>
  );
}

export function ApiOffline({ what = "this page" }) {
  return (
    <div
      data-testid="api-offline-state"
      className="bka-container flex min-h-[45vh] max-w-2xl flex-col items-center justify-center gap-4 py-20 text-center"
    >
      <div className="rounded-full bg-bka-aqua/40 px-4 py-1 text-xs font-bold uppercase tracking-[0.18em] text-bka-emeraldDark">
        Content service unavailable
      </div>
      <h1 className="font-display text-3xl font-bold text-bka-navy sm:text-4xl">
        We can’t load {what} right now
      </h1>
      <p className="text-base text-bka-ink/75">
        The website is running, but it cannot reach the content service that stores our pages,
        programmes and Emerald Honours information. This is a server configuration issue, not a
        problem with your device.
      </p>
      <p className="text-sm text-bka-ink/60">
        Please try again shortly. If you need assistance in the meantime, contact the Bahamas
        Kidney Association directly.
      </p>
    </div>
  );
}

export function Loading({ label = "Loading" }) {
  return (
    <div data-testid="loading-state" className="flex min-h-[45vh] items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <div className="h-9 w-9 animate-spin rounded-full border-[3px] border-bka-aqua border-t-bka-emerald" />
        <p className="text-sm font-semibold text-bka-ink/60">{label}…</p>
      </div>
    </div>
  );
}

/** Official Emerald Honours award asset, used as-is (no SVG/CSS recreation). */
export function EmeraldGem({ className = "" }) {
  return (
    <img
      src={asset("/assets/emerald-honours-award-transparent.png")}
      alt="Emerald Honours Award"
      data-testid="emerald-honours-award-image"
      width={388}
      height={452}
      decoding="async"
      className={`emerald-feature-card__award ${className}`}
    />
  );
}

export function Disclaimer({ title, body, testId = "medical-disclaimer" }) {
  return (
    <aside
      data-testid={testId}
      className="rounded-2xl border-l-4 border-bka-orange bg-bka-orangeTint p-6 lg:p-8"
      role="note"
    >
      <p className="bka-overline mb-2 text-bka-orangeDark">{title || "Important"}</p>
      <p className="text-sm leading-[1.8] text-bka-ink/90">{body}</p>
    </aside>
  );
}
